import { useMutation, useQueryClient } from '@tanstack/react-query';

import { internshipApi } from '@/api';
import { useToast } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { errorMessage } from '@/hooks/useApiError';

/**
 * Save/unsave toggle shared by discovery, detail and the saved list.
 * Invalidates every internship query so the bookmark state stays consistent.
 */
export function useSaveInternship() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();

  const mutation = useMutation({
    mutationFn: async (internship) => {
      if (internship.is_saved) {
        await internshipApi.unsaveInternship(internship.id);
        return { saved: false };
      }
      await internshipApi.saveInternship(internship.id);
      return { saved: true };
    },
    onSuccess: (result) => {
      toast.success(result.saved ? 'Internship saved' : 'Removed from saved');
      void queryClient.invalidateQueries({ queryKey: ['internships'] });
      void queryClient.invalidateQueries({ queryKey: ['saved-internships'] });
      void queryClient.invalidateQueries({ queryKey: ['recommendations'] });
      void queryClient.invalidateQueries({ queryKey: ['student-dashboard'] });
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not update saved list')),
  });

  return {
    toggle: mutation.mutate,
    isPending: mutation.isPending,
    pendingId: mutation.variables?.id,
    /** Only students can bookmark, so the button is hidden for everyone else. */
    canSave: user?.role === 'STUDENT',
  };
}
